import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { toast } from "../../utils/toast";
import { bursariesApi } from "../../api/hliLists";
import { abroadAdminApi } from "../../api/abroadLoans";
import { errorMessageOf, pageParams, scholarshipsApi } from "../../api/scholarships";

// Where a scholarship's applications stand, from the scholarships report, in journey order
const APPLICATION_STAGES = [
  ["inReview", "Under evaluation"],
  ["reviewed", "Marked"],
  ["awarded", "Nominated"],
  ["reserve", "Reserve"],
  ["notAwarded", "Not nominated"],
  ["withDg", "With the DG"],
  ["withSponsor", "With the partner"],
  ["granted", "Granted"],
  ["rejected", "Not granted"],
  ["withdrawn", "Withdrawn"],
];

// Open, not yet open or closed: a scholarship closed early, or decided, is closed whatever its dates
const scholarshipWindowOf = (s, now) => (s.state !== "PUBLISHED" ? "closed" : new Date(s.opensAt) > now ? "upcoming" : new Date(s.closesAt) > now ? "open" : "closed");
const callWindowOf = (c, now) => (c.open ? "open" : new Date(c.opensAt) > now ? "upcoming" : "closed");

/**
 * A year's sponsored scholarships and abroad loan calls by application window, and the
 * scholarships' applications by stage: the Scholarships service's list and report, and the
 * abroad loan calls (Loans service), by the year's id.
 */
export const useYearScholarships = (academicYearId) =>
  useQuery({
    queryKey: ["dashboard", "scholarships", academicYearId],
    enabled: Boolean(academicYearId),
    // Another year's figures replace the shown ones once they arrive
    placeholderData: keepPreviousData,
    staleTime: 60 * 1000,
    queryFn: async () => {
      try {
        const [list, report, calls] = await Promise.all([
          scholarshipsApi.get("staff/scholarships", { params: pageParams({ academicYearId, size: 100 }) }),
          scholarshipsApi.get("staff/reports/scholarships", { params: { academicYearId } }),
          abroadAdminApi.calls(pageParams({ kind: "LOAN", state: "PUBLISHED", size: 100 })),
        ]);
        const now = new Date();
        const windows = [
          ...(list.data?.content ?? []).filter((s) => !["DRAFT", "CANCELLED"].includes(s.state)).map((s) => scholarshipWindowOf(s, now)),
          ...(calls.data?.items ?? []).filter((c) => c.academicYearId === academicYearId).map((c) => callWindowOf(c, now)),
        ];
        const count = (w) => windows.filter((x) => x === w).length;
        const rows = report.data ?? [];
        return {
          windows: { total: windows.length, open: count("open"), upcoming: count("upcoming"), closed: count("closed") },
          submitted: rows.reduce((sum, r) => sum + r.submitted, 0),
          stages: APPLICATION_STAGES.map(([key, label]) => ({ key, label, value: rows.reduce((sum, r) => sum + (r[key] ?? 0), 0) })),
        };
      } catch (error) {
        console.error(`Failed to load the scholarship figures of academic year ${academicYearId}:`, error);
        toast.error(errorMessageOf(error, "Failed to load scholarship figures"), { id: "dashboard-scholarships-error" });
        throw error;
      }
    },
  });

// A year's bursary counts, selections and result claims (Loans service), by the year's id
export const useBursarySummary = (academicYearId) =>
  useQuery({
    queryKey: ["bursaries", "summary", academicYearId],
    enabled: Boolean(academicYearId),
    placeholderData: keepPreviousData,
    staleTime: 60 * 1000,
    queryFn: async () => {
      try {
        return (await bursariesApi.summary(academicYearId)).data;
      } catch (error) {
        console.error("Failed to load the bursary summary:", error);
        toast.error(errorMessageOf(error, "Failed to load bursary figures"), { id: "bursary-summary-error" });
        throw error;
      }
    },
  });
